import api from '../httpClient';

export const AVAILABILITY_WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

export type AvailabilityWeekday = (typeof AVAILABILITY_WEEKDAYS)[number];

export interface WeeklyAvailabilitySlot {
  /** Local HH:mm — use for nutritionist UI display and edit */
  startTime: string;
  endTime: string;
  /** UTC HH:mm — reference only */
  startTimeUtc?: string;
  endTimeUtc?: string;
  startUtcDayOffset?: number;
  endUtcDayOffset?: number;
}

export interface WeeklyAvailabilityDay {
  day: AvailabilityWeekday;
  isEnabled: boolean;
  slots: WeeklyAvailabilitySlot[];
}

export interface WeeklyAvailabilitySchedule {
  /** IANA timezone saved with the schedule, e.g. `Europe/London` */
  timezone?: string;
  days: WeeklyAvailabilityDay[];
  /**
   * The backend-owned marker for whether the nutritionist has initialized or
   * intentionally saved their schedule. Empty days are valid when this is true.
   */
  availabilityInitialized: boolean;
}

export interface UpdateWeeklyAvailabilitySchedulePayload
  extends Omit<WeeklyAvailabilitySchedule, 'availabilityInitialized'> {
  /** IANA timezone the schedule wall times belong to, e.g. `Europe/London` */
  timezone: string;
  copyFromDay?: AvailabilityWeekday;
  copyToDays?: AvailabilityWeekday[];
}

export interface InitializeWeeklyAvailabilitySchedulePayload {
  timezone?: string;
}

export interface InitializeWeeklyAvailabilityScheduleResponse extends WeeklyAvailabilitySchedule {
  message: string;
  availabilityInitialized: true;
}

export interface RecurringBreak {
  id: string;
  name: string;
  days: AvailabilityWeekday[];
  /** Local HH:mm — use for display and edit */
  startTime: string;
  endTime: string;
  /** UTC HH:mm — reference only */
  startTimeUtc?: string;
  endTimeUtc?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateRecurringBreakPayload {
  name: string;
  days: AvailabilityWeekday[];
  startTime: string;
  endTime: string;
}

export interface TimeOff {
  id?: string;
  _id?: string;
  startDate: string;
  endDate: string;
  durationDays?: number;
  reason?: string;
  conflictCount?: number;
  conflicts?: TimeOffConflict[];
  createdAt?: string;
  updatedAt?: string;
}

export interface TimeOffConflict {
  [key: string]: unknown;
  id?: string | number;
  _id?: string | number;
  appointmentId?: string | number;
  appointment_id?: string | number;
  date?: string;
  startTime?: string;
  start_time?: string;
  endTime?: string;
  end_time?: string;
  title?: string;
  type?: string;
  appointmentType?: string;
  appointment_type?: string;
  mode?: string;
  location?: string;
  clientName?: string;
  client_name?: string;
}

export interface CreateTimeOffPayload {
  startDate: string;
  endDate: string;
  reason: string;
}

export const getWeeklyAvailabilitySchedule = async (): Promise<WeeklyAvailabilitySchedule> => {
  const response = await api.get<WeeklyAvailabilitySchedule>('/availability/schedule');
  return response.data;
};

export const initializeWeeklyAvailabilitySchedule = async (
  payload?: InitializeWeeklyAvailabilitySchedulePayload,
): Promise<InitializeWeeklyAvailabilityScheduleResponse> => {
  const response = await api.post<InitializeWeeklyAvailabilityScheduleResponse>(
    '/availability/schedule/initialize',
    payload,
  );
  return response.data;
};

export const updateWeeklyAvailabilitySchedule = async (
  payload: UpdateWeeklyAvailabilitySchedulePayload,
): Promise<WeeklyAvailabilitySchedule> => {
  const response = await api.put<WeeklyAvailabilitySchedule>('/availability/schedule', payload);
  return response.data;
};

export const getRecurringBreaks = async (): Promise<RecurringBreak[]> => {
  const response = await api.get<RecurringBreak[]>('/availability/breaks');
  return response.data;
};

export const createRecurringBreak = async (
  payload: CreateRecurringBreakPayload,
): Promise<RecurringBreak> => {
  const response = await api.post<RecurringBreak>('/availability/breaks', payload);
  return response.data;
};

export const updateRecurringBreak = async (
  id: string,
  payload: CreateRecurringBreakPayload,
): Promise<RecurringBreak> => {
  const response = await api.put<RecurringBreak>(`/availability/breaks/${id}`, payload);
  return response.data;
};

export const deleteRecurringBreak = async (id: string): Promise<void> => {
  await api.delete(`/availability/breaks/${id}`);
};

export const getTimeOff = async (): Promise<TimeOff[]> => {
  const response = await api.get<TimeOff[]>('/availability/time-off');
  return response.data;
};

export const createTimeOff = async (payload: CreateTimeOffPayload): Promise<TimeOff> => {
  const response = await api.post<TimeOff>('/availability/time-off', payload);
  return response.data;
};

export const deleteTimeOff = async (id: string): Promise<void> => {
  await api.delete(`/availability/time-off/${id}`);
};
